import * as React from 'react';
import {
  MdAdd,
  MdSearch,
  MdClose,
  MdCheckCircle,
  MdVisibility,
  MdEdit,
  MdPauseCircle,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { claimStatusList as fetchClaimStatuses } from '../../Services/Claim360Service';
import ExportListButton from '../../Components/Claim360/ExportListButton';
import { formatUtcToLocalDate } from '../../Utils/DateTime';
import { claimStatusLabel as getStatusLabel, isClaimRejectionLabel } from '../../Utils/stediUtils';

const STATUS_OPTIONS = ['All', 'Finalized', 'Denied', 'Pending', 'Acknowledged', 'Info Requested', 'Missing Info', 'Invalid Info', 'Inconsistent Info', 'Unable to Process', 'Rejected', 'Payer Unavailable', 'Error', 'Not Found'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));

export default function ClaimStatusList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewClaimStatus = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_VIEW, decodedToken);
  const canCreateClaimStatus = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_CREATE, decodedToken);
  const canEditClaimStatus = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_EDIT, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loading, setLoading] = React.useState(false);

  const handleCreateClaimStatus = React.useCallback(() => {
    navigate(APPRoutes.CLAIM_STATUS_FORM);
  }, [navigate]);

  const loadClaimStatuses = React.useCallback(async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetchClaimStatuses(token);
      const payload = response?.data ?? response;
      const recordCollections = [
        payload,
        payload?.claim_status_logs,
        payload?.claimStatusLogs,
        payload?.items,
        payload?.records,
        payload?.logs,
        payload?.results,
        payload?.result,
        payload?.data,
        payload?.data?.claim_status_logs,
        payload?.data?.claimStatusLogs,
        payload?.data?.items,
        payload?.data?.records,
        payload?.data?.logs,
      ];
      const claimStatusRecords = recordCollections.find(Array.isArray);

      if (!claimStatusRecords) {
        throw new Error('The claim status API response did not contain a records array. Check the response in the browser Network tab.');
      }

      const mappedRecords = claimStatusRecords.map((record) => ({
        ...record,
        status_name: getStatusLabel(record),
        subscriber_name: record.patientName || '-',
        organization_name: record.organizationName || '-',
        member_id: record.memberId || '-',
        payer_name: record.payerName || record.payerId || '-',
        payer_claim_number: record.payerClaimNumber || '-',
        checked_on: record.checkedOn ?? null,
      }));

      setRows(mappedRecords);
      setStatusSummary({
        total: mappedRecords.length,
        active: mappedRecords.filter((record) => record.status_name === 'Finalized').length,
        inactive: mappedRecords.filter((record) => (['Denied', 'Rejected'].includes(record.status_name) || isClaimRejectionLabel(record.status_name))).length,
      });
    } catch (error) {
      console.error('Failed to load claim statuses:', error);
      toast.error(error.message || 'Unable to load claim statuses.');
      setRows([]);
      setStatusSummary({ total: 0, active: 0, inactive: 0 });
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadClaimStatuses();
    }
  }, [token, loadClaimStatuses]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        status === 'All' || row.status_name === status;
      const matchesSearch =
        !term ||
        [row.subscriber_name, row.member_id, row.payer_name, row.payer_claim_number, row.organization_name]
          .some((value) => String(value || '').toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total Claim Statuses',
        value: statusSummary.total,
        icon: <MdAdd size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Finalized',
        value: statusSummary.active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Denied / Unable to Process',
        value: statusSummary.inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ],
    [statusSummary],
  );

  const columns = React.useMemo(() => {
    const cols = [
      { id: 'subscriber_name', label: 'Patient', minWidth: 200 },
      { id: 'member_id', label: 'Member ID', minWidth: 140 },
      { id: 'payer_name', label: 'Payer', minWidth: 200 },
      { id: 'status_name', label: 'Status', minWidth: 140 },
      { id: 'organization_name', label: 'Organization', minWidth: 180 },
      { id: 'checked_on', label: 'Checked On', minWidth: 140, format: (value) => formatUtcToLocalDate(value) },
    ];

    if (canViewClaimStatus || canEditClaimStatus) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewClaimStatus ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => navigate(APPRoutes.CLAIM_STATUS_VIEW.replace(':id', row.id), { state: { claimStatus: row } })}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditClaimStatus ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.CLAIM_STATUS_EDIT.replace(':id', row.id), { state: { claimStatus: row } })}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    canViewClaimStatus,
    canEditClaimStatus,
  ]);

  return (
    <>
      <PageTitle title="Claim Status - Aegis 360" />
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Claim Status</h4>
          <p className="text-secondary mb-0 small">
            Manage subscriber Claim status verification records.
          </p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 mt-md-0 mt-2">
          <ExportListButton kind="claim-status" rows={filteredRows} disabled={loading} />
          {canCreateClaimStatus ? (
            <button
              onClick={handleCreateClaimStatus}
              type="button"
              className="btn hc-bg-primary w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
            >
              <MdAdd size={20} />
              <span>Check Claim Status</span>
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search first name, last name or organization"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

            <SearchableDropdown
              placeholder="Select status"
              options={STATUS_DROPDOWN_OPTIONS}
              value={status}
              onChange={setStatus}
              showSearch={true}
            />

          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>
    </>
  );
}
